/**
 * Lets exactly one tab of the app do a piece of work at a time. Every tab
 * shares the same event log and the same cursors, so two tabs sending, or
 * two tabs updating the reader's stats, would each do the same events.
 *
 * Web Locks where the browser has them: a tab waits its turn, and by the
 * time it runs, the cursors already cover what the other tab did. Where it
 * has not (Safari before 15.4), a short lease in `localStorage` does the
 * same job less strictly: a tab that finds a live lease skips this round and
 * is tried again on its next trigger.
 */
const LEASE_MS = 15_000;

const tabId = Math.random().toString(36).slice(2);

function readLease(key: string): { id: string; until: number } | undefined {
	try {
		const lease = JSON.parse(localStorage.getItem(key) ?? 'null');
		return lease && typeof lease.id === 'string' && typeof lease.until === 'number'
			? lease
			: undefined;
	} catch {
		return undefined;
	}
}

async function withLease(
	name: string,
	run: () => Promise<void>,
): Promise<void> {
	const key = `${name}-lease`;
	const now = Date.now();
	const current = readLease(key);
	if (current && current.id !== tabId && current.until > now) {
		return;
	}
	try {
		localStorage.setItem(
			key,
			JSON.stringify({ id: tabId, until: now + LEASE_MS }),
		);
		// Another tab may have written between the read and the write.
		if (readLease(key)?.id !== tabId) {
			return;
		}
	} catch {
		// No storage: nothing to coordinate through, and no other tab can
		// see this one's cursors either.
	}
	try {
		await run();
	} finally {
		try {
			if (readLease(key)?.id === tabId) {
				localStorage.removeItem(key);
			}
		} catch {
			// The lease expires on its own.
		}
	}
}

export function withTabLock(
	name: string,
	run: () => Promise<void>,
): Promise<void> {
	if (typeof navigator !== 'undefined' && navigator.locks?.request) {
		return navigator.locks.request(name, run) as Promise<void>;
	}
	return withLease(name, run);
}

/** Exported for tests of the fallback. */
export { withLease };
